<template>
  <PaymentDetailCards :title="subscriptionStore.userPlan" :buttons="listButtonBottom">
    <dl class="wrapper-detail">
      <dt class="medium">{{ t('add_credit_estimated_credit') }}</dt>
      <dd>
        <div class="dd-crd-wrap">
          <span>{{ creditsStatus }}</span>
        </div>

        <button type="button" class="credit-buy-btn" @click="handleCreditPurchase">
          <img :src="getAssetImageUrl('common/icons/credit-white-icon.svg')" alt="" />
          <span>{{ t('label_purchase') }}</span>
        </button>
      </dd>

      <template v-if="paymentIntervalStatus">
        <dt>{{ t('self_service_payment_interval') }}</dt>
        <dd>{{ paymentIntervalStatus }}</dd>
      </template>

      <dt>{{ t('self_service_price') }}</dt>
      <dd>{{ billingAmount }}</dd>

      <template v-if="subscriptionStore.currentPlanStatus !== 'CANCEL' && subscriptionStore.currentPlan !== 'FREE'">
        <dt>{{ t('self_service_date_next_billing') }}</dt>
        <dd>{{ nextBillingDate }}</dd>
      </template>

      <template v-if="subscriptionStore.currentPlan === 'FREE'">
        <dt>{{ t('next_credit_refill_date') }}</dt>
        <dd>{{ nextBillingDate }}</dd>
      </template>

      <template v-if="subscriptionStore.currentPlanStatus === 'PLANNED'">
        <dt>{{ t('planned_plan', { plan: 'Starter' }) }}</dt>
        <dd>
          {{ plannedStartDate }}
          <button type="button" class="cancel-btn" @click="handlePlannedCancel">
            {{ t('ss_cancel_planned') }}
          </button>
        </dd>
      </template>

      <template v-if="subscriptionStore.currentPlanStatus === 'CANCEL'">
        <dt>{{ t('date_end_of_service') }}</dt>
        <dd>{{ planEndDate }}</dd>
      </template>

      <dt>{{ t('self_service_auto_refill') }}</dt>
      <dd>
        <div>
          <ToggleSwitch
            :model-value="subscriptionStore.currentPlanDetails?.rechargeAllow"
            @click.prevent="handleRechargeToggle"
          />
        </div>
      </dd>
    </dl>
  </PaymentDetailCards>
</template>

<script setup lang="ts">
import { cancelSubscriptionReservation } from '@/api/apiService';
import PaymentDetailCards from '@/components/domains/self-service/payment-settings/PaymentDetailCards.vue';
import PopupReservationCancel from '@/components/domains/self-service/payment-settings/PopupReservationCancel.vue';
import PopupSmall from '@/components/shared/composite/PopupSmall.vue';
import ToggleSwitch from '@/components/shared/composite/ToggleSwitch.vue';
import { useGtag } from '@/composables/shared/useGtag';
import { useNotification } from '@/composables/shared/useNotification';
import { usePayment } from '@/composables/shared/usePayment';
import { usePopupStore } from '@/stores/popup';
import { useSubscriptionStore } from '@/stores/subscription';
import { IButtonDynamic } from '@/types/components';
import { dateFormatDot } from '@/utils/dateFormatter.js';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { formatNumberWithComma } from '@/utils/stringUtils.js';

const { t } = useI18n();
const router = useRouter();

const subscriptionStore = useSubscriptionStore();
const popupStore = usePopupStore();

const {
  openCreditAutoRechargePopup,
  hasRegisteredCard,
  openCardRegisterPopup,
  openCreditBuyPopup,
  openProUpgradePopup,
  cancelAutoRecharge,
  openSubscriptionPaymentPopup,
  openCardManagePopup,
} = usePayment();
const { alertAutoRenewDeactivate } = useNotification();
const { trackEventFunnel, trackEventClick } = useGtag();

const isMonthly = computed(() => subscriptionStore.currentPlanDetails?.billingCycle === 'MONTHLY');

const creditsStatus = computed(() => {
  return `${formatNumberWithComma(subscriptionStore.currentCredits)} / ${formatNumberWithComma(subscriptionStore.purchasedCredits)}`;
});

const plannedStartDate = computed(() => {
  if (subscriptionStore.currentPlanStatus !== 'PLANNED') return null;

  return dateFormatDot(subscriptionStore.plannedPlanDetails?.startDate);
});

const paymentIntervalStatus = computed(() => {
  if (subscriptionStore.currentPlan === 'FREE') {
    return '';
  }

  if (subscriptionStore.currentPlanStatus === 'CANCEL') {
    return t('self_service_subscription_cancelled');
  }

  return isMonthly.value ? t('self_service_billing_interval_monthly') : t('self_service_billing_interval_yearly');
});

const planEndDate = computed(() => {
  if (subscriptionStore.currentPlan === 'FREE') {
    return '';
  }

  return dateFormatDot(new Date(subscriptionStore.currentPlanDetails?.endDate ?? ''));
});

const billingAmount = computed(() => {
  if (subscriptionStore.currentPlan === 'FREE') {
    return `${t('ss_price_free')}`;
  }

  const planPricing = subscriptionStore.planPrices?.[subscriptionStore.currentPlan]?.[subscriptionStore.currency];
  const price = isMonthly.value ? planPricing.pricePerMonth : planPricing.pricePerYear;

  return subscriptionStore.currency === 'KRW'
    ? `${formatNumberWithComma(price)}${t('label_currency_won')}`
    : `$${formatNumberWithComma(price)}`;
});

const nextBillingDate = computed(() => {
  if (subscriptionStore.currentPlan === 'FREE') {
    return dateFormatDot(subscriptionStore.currentPlanDetails?.nextPaymentDate);
  }

  if (subscriptionStore.currentPlanStatus === 'CANCEL') {
    return t('self_service_subscription_cancelled');
  }

  return subscriptionStore.plannedPlanDetails
    ? dateFormatDot(subscriptionStore.plannedPlanDetails.nextPaymentDate)
    : dateFormatDot(subscriptionStore.currentPlanDetails?.nextPaymentDate);
});

const listButtonBottom = computed(() => {
  const listButton: IButtonDynamic[] = [];
  const manageButton = {
    type: 'manage',
    label: t('self_service_manage_card'),
    onClick: () => handleCardManage(),
  };
  const upgradeButton = {
    type: 'payment',
    label: t('upgrade_subscription'),
    onClick: () => goToPlanUpgrade(),
  };

  listButton.push(manageButton);

  if (subscriptionStore.currentPlan !== 'PRO') {
    listButton.push(upgradeButton);
  }

  return listButton;
});

const goToPlanUpgrade = () => {
  trackEventClick('plan_manage', '구독 업그레이드');
  router.push({ name: 'ss-plan-info' });
};

const handleCardManage = async () => {
  if (!hasRegisteredCard()) {
    const registeredCardType = await openCardRegisterPopup();
    if (!registeredCardType) {
      return;
    }
  }

  openCardManagePopup('MANAGE', subscriptionStore.paymentType);
};

const handleCreditPurchase = async () => {
  trackEventFunnel('click_credit_buy_button');

  if (!hasRegisteredCard()) {
    const registeredCardType = await openCardRegisterPopup();
    if (!registeredCardType) {
      return;
    }
  }

  if (subscriptionStore.lastPaymentErrorCode) {
    const selectedCardType = await openCardManagePopup('PAYMENT', subscriptionStore.paymentType);
    if (!selectedCardType) {
      return;
    }
  }

  openCreditBuyPopup();
};

const handleRechargeToggle = async () => {
  trackEventFunnel('click_auto_recharge_toggle');

  if (subscriptionStore.currentPlan !== 'PRO') {
    openProUpgradePopup(handleProUpgrade);
    return;
  }

  const { rechargeAllow } = subscriptionStore.currentPlanDetails ?? { rechargeAllow: false };
  if (rechargeAllow) {
    trackEventFunnel('open_auto_recharge_cancel_popup');
    alertAutoRenewDeactivate(cancelAutoRecharge);
  } else {
    openCreditAutoRechargePopup();
  }
};

const handleProUpgrade = async () => {
  trackEventFunnel('click_pro_upgrade_button');

  if (!hasRegisteredCard()) {
    const registeredCardType = await openCardRegisterPopup();
    if (!registeredCardType) {
      return;
    }

    const selectedCardType = await openCardManagePopup('PAYMENT', registeredCardType);
    if (!selectedCardType) {
      return;
    }
  }

  openSubscriptionPaymentPopup('PRO', subscriptionStore.currentPlanDetails?.billingCycle);
};

const handlePlannedCancel = () => {
  const popupId = popupStore.open(PopupSmall, {
    contentSlot: h(PopupReservationCancel),
    cancelButton: { text: t('label_keep_planned') },
    confirmButton: { text: t('ss_cancel_planned'), action: () => cancelPlanned() },
  });

  const cancelPlanned = async () => {
    await cancelSubscriptionReservation();
    await subscriptionStore.fetchSubscriptionInfo();

    popupStore.close(popupId);
  };
};
</script>

<style scoped lang="scss">
.wrapper-detail {
  dl {
    dd {
      font-weight: 500;
      .dd-crd-wrap {
        span {
          &:nth-child(2) {
            color: $color-gray-100;
          }
        }
      }
    }
  }
}

.credit-buy-btn {
  background: #ffd609;
  border-radius: 100px;
  padding: 6px 12px 6px 6px;
  display: flex;
  gap: $size-xs;
  &:hover {
    background: #ecc500;
  }
  span {
    font-size: $font-size-psmall;
    font-weight: 600;
  }
}

.cancel-btn {
  border-bottom: 1px solid $color-gray-200;
  color: $color-gray-200;
  font-size: $font-size-psmall;
}

@include respond-to(mobile-portrait) {
  .wrapper-detail {
    grid-template-columns: 1fr !important;
    dt {
      padding-top: 0.5rem;
    }
    dd {
      justify-self: start !important;
      width: 100%;
      border-bottom: 1px solid $color-gray-40;
      padding-bottom: 1rem;
    }
  }
}
</style>
